/**
 * Schedule Modal — global modal rendered in dashboard layout.
 * Watches `scheduleModalRequested` from page-context store and opens
 * TaskConfirmationModal pre-configured for schedule editing.
 */

import { useState } from "react";
import { toast } from "sonner";
import {
	type ScheduleConfig,
	TaskConfirmationModal,
	type TaskFormData,
} from "@/components/task-confirmation-modal";
import {
	useCreateSchedule,
	useDeleteSchedule,
	useSchedules,
	useTask,
	useUpdateTask,
} from "@/lib/api/hooks/tasks";
import { buildScheduleParams } from "@/lib/utils/schedule";
import { usePageContextStore } from "@/stores/page-context-store";

export function ScheduleModal() {
	const { scheduleAction, scheduleModalRequested } = usePageContextStore();
	const clearRequest = () => usePageContextStore.setState({ scheduleModalRequested: false });

	const taskId = scheduleAction?.taskId ?? 0;
	const agentInstanceId = scheduleAction?.agentInstanceId ?? 0;
	const { data: task } = useTask(taskId, !!taskId);
	const { data: schedulesData } = useSchedules();
	const schedules = schedulesData?.schedules || [];
	const updateTask = useUpdateTask(taskId);
	const createSchedule = useCreateSchedule();
	const deleteSchedule = useDeleteSchedule();

	// Derive schedule defaults
	const existingSchedule = schedules.find((s) => s.task_id === taskId);
	const defaultSchedule: ScheduleConfig = existingSchedule
		? {
				triggerType: existingSchedule.trigger_type === "interval" ? "interval" : "cron",
				intervalSeconds: existingSchedule.interval_seconds ?? undefined,
				cronExpression: existingSchedule.cron_expression ?? undefined,
				timezone: existingSchedule.timezone ?? "UTC",
			}
		: {
				triggerType: "cron",
				cronExpression: "0 9 * * *",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			};

	const [isSubmitting, setIsSubmitting] = useState(false);
	const isOpen = scheduleModalRequested && !!scheduleAction && !!task;

	const handleSubmit = async (formData: TaskFormData) => {
		if (!task) return;
		setIsSubmitting(true);
		try {
			// Update task metadata
			await updateTask.mutateAsync({
				name: formData.name,
				instructions: formData.instructions,
				expected_output: formData.expectedOutput || null,
				goals: formData.goals || null,
				integration_ids: formData.integrations.length > 0 ? formData.integrations : null,
				email_notifications: formData.emailNotifications,
				permission_mode: formData.permissionMode,
			});

			// Delete old schedule if editing
			if (existingSchedule) {
				const scheduleId = Number.parseInt(existingSchedule.job_id.replace("schedule_", ""), 10);
				await deleteSchedule.mutateAsync(scheduleId);
			}

			if (formData.schedule === "now") {
				toast.success(existingSchedule ? "Schedule removed" : "Task updated");
			} else {
				const { cronExpression, intervalSeconds } = buildScheduleParams(formData);

				await createSchedule.mutateAsync({
					task_id: taskId,
					agent_instance_id: agentInstanceId,
					cron_expression: cronExpression,
					interval_seconds: intervalSeconds,
					run_date: null,
					timezone: formData.timezone || "UTC",
				});
				toast.success(existingSchedule ? "Schedule updated" : "Schedule created");
			}
			clearRequest();
		} catch (error) {
			console.error("Failed to save schedule:", error);
			toast.error("Failed to save schedule. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isOpen) return null;

	return (
		<TaskConfirmationModal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) clearRequest();
			}}
			instructions={task.instructions}
			onSubmit={handleSubmit}
			isSubmitting={isSubmitting}
			isEditMode
			defaultName={task.name}
			defaultIntegrations={task.integration_ids || []}
			defaultGoals={task.goals || ""}
			showInstructionsField
			defaultSchedule={defaultSchedule}
			defaultPermissionMode={(task.permission_mode as TaskFormData["permissionMode"]) || "safe"}
			hideEmailInboxSection={true}
		/>
	);
}
